import { jwtDecode } from "jwt-decode";

import type { RoleDTO } from "../../common/constants/userRole";
import type { UserDTO } from "../../common/types/UserDTO";

export const TOKEN_KEY = "access_token";

interface TokenType {
  sub: string; // user id - impersonated or actual
  aid: string; // account id - impersonated or actual
  upn: string; // email
  groups: RoleDTO[];
  // act is present when in impersonation mode
  act?: {
    sub: string; // user id
    email: string;
  };
  [key: string]: any;
}

class TokenManager {
  private listeners = new Set<() => void>();

  constructor() {
    // Keep React subscribers in sync when another tab sets/clears the token.
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (event) => {
        if (event.key === TOKEN_KEY) this.emit();
      });
    }
  }

  // useSyncExternalStore subscribe. Callers re-render on any token change, so
  // reads during render stay correct after the OIDC cookie is promoted to
  // localStorage on first load (see useInit) rather than freezing at the
  // first-render value under React Compiler.
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emit() {
    this.listeners.forEach((listener) => listener());
  }

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
    this.emit();
  }

  removeToken() {
    localStorage.removeItem(TOKEN_KEY);
    this.emit();
  }

  getDecodedToken(): TokenType | undefined {
    const token = this.getToken();
    if (!token) return undefined;

    try {
      return jwtDecode(token);
    } catch {
      // Silently remove any invalid token
      this.removeToken();
      return undefined;
    }
  }

  getUserData() {
    const decodedToken = this.getDecodedToken();

    if (!decodedToken) return undefined;

    const userId = parseInt(decodedToken.sub);
    const userEmail = decodedToken.upn;

    const userData: UserDTO = {
      id: userId,
      email: userEmail,
      accountId: parseInt(decodedToken.aid),
      roles: decodedToken.groups,
    };

    return userData;
  }

  isImpersonationMode() {
    const decodedToken = this.getDecodedToken();
    return Boolean(decodedToken?.act);
  }
}

const tokenManager = new TokenManager();

export default tokenManager;
